class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.defaultHTML = this.predictiveSearchResults.innerHTML;
    this.clearSearchButton = this.querySelector('.reset__button');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isClickingProduct = false;
    this.hasResults = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' || event.code === 'Escape') {
        this.close();
        this.input.blur();
        this.handleClearSearch();
      }
    });

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.clearSearchButton.addEventListener('click', this.handleClearSearch.bind(this));
    document.addEventListener('click', this.handleDocumentClick);
  }

  getQuery() {
    const query = this.input.value.trim();
    return query;
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    //Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    this.getSearchResults(this.searchTerm);

    if (this.searchTerm.length) {
      this.updateSeeAllButtons(this.searchTerm);
    } else {
      this.predictiveSearchResults.innerHTML = this.defaultHTML;
    }
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.removeStickyClass();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    this.open();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      //Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    return;
  }

  handleDocumentClick(event) {
    if (!this.hasAttribute('open')) return;
    
    const isInsidePredictiveSearch = this.contains(event.target);
    
    const isProductClick = event.target.closest('.predictive-search__item') ||
                          event.target.closest('.swiper-slide') ||
                          event.target.closest('.predictive-search-swiper') ||
                          event.target.closest('.swiper-scrollbar') ||
                          event.target.closest('.swiper-scrollbar-drag');
    
    if (!isInsidePredictiveSearch && !isProductClick) {
      this.close();
      this.removeStickyClass();
    }
  }

  handleClearSearch(event) {
    event.preventDefault();
    this.input.value = '';
    this.onChange();
  }

  updateSeeAllButtons(searchTerm) {
    setTimeout(() => {
      document.querySelectorAll('[js-see-all-results]').forEach(button => {
        button.setAttribute('href', `/search?q=${searchTerm}`);
      });
    }, 1000);
  }

  onKeyup(event) {
    //Handle navigation keys
    switch (event.code) {
    case 'ArrowUp':
      event.preventDefault();
      this.switchOption('up');
      break;
    case 'ArrowDown':
      event.preventDefault();
      this.switchOption('down');
      break;
    case 'Enter':
      event.preventDefault();
      this.selectOption();
      break;
    default:
      break;
    }
  }

  onKeydown(event) {
    //Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      const matches = currentButtonText.match(new RegExp(previousTerm, 'g'));
      if (matches && matches.length > 1) {
        //The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    //Filter out hidden elements (duplicated page and article resources) thanks
    //to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const productsLimit = window.theme?.settings?.predictive_search_products_limit || 8;
    const searchUrl = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[limit]=${productsLimit}&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title`;
    
    fetch(searchUrl, {
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.hasResults = !!new DOMParser()
          .parseFromString(text, 'text/html').querySelector('.card__information');
        //Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          //Code 20 means the call was aborted
          return;
        }
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const sectionHeader = document.querySelector('.section-header');
    if (sectionHeader) {
      sectionHeader.classList.add('shopify-section-header-sticky');
    }
    this.resultsMaxHeight =
      window.innerHeight - sectionHeader?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  removeStickyClass() {
    const sectionHeader = document.querySelector('.section-header');
    if (sectionHeader && window.scrollY === 0) {
      sectionHeader.classList.remove('shopify-section-header-sticky');
    }
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.removeStickyClass();
  }

  disconnectedCallback() {
    if (this.handleDocumentClick) {
      document.removeEventListener('click', this.handleDocumentClick);
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
